import GoBackButton from "@/components/go-back-button";
import Layout from "@/components/layout";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styles from "./index.module.scss";
import { useNavigate } from "react-router-dom";
import { Routes } from "@/types/routes";
import OptionCard from "@/components/option-card";
import { useQuery } from "@tanstack/react-query";
import api from "@/services/api";
import LoadingScreen from "@/components/loading-screen";
import ErrorScreen from "@/components/error-screen";
import { format, isTomorrow } from "date-fns";
import { capitalize } from "lodash";
import usePickupStore from "@/stores/pickup";
import { StillThereModal } from "@/components/still-there-modal";

const PickupTimeSlotPage: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setTimeSlot } = usePickupStore();

  const { isLoading, isError, data, refetch } = useQuery(["pickup-times"], api.timeSlots.get, {
    select: (response) => response.data.data,
  });

  const onBackClick = () => {
    navigate(Routes.MAIN);
  };

  const getTitle = (startAt: string) => {
    const date = new Date(startAt);
    if (isTomorrow(date)) return t("pickup.timeSlotPage.tomorrow");
    const lowerCasedResult = format(date, "EEEE PPP").split(",")[0];
    return capitalize(lowerCasedResult);
  };

  const getInfo = (startAt: string) => {
    const date = new Date(startAt);
    return `${t("ui.from")} ${format(date, "kk:mm")}`;
  };

  const onSelectOption = (startAt: string) => {
    setTimeSlot(startAt);
    navigate(Routes.YOUR_INFO);
  };

  if (isLoading) return <LoadingScreen />;
  if (isError) return <ErrorScreen onTryAgain={refetch} />;

  return (
    <Layout childrenClassName={styles.container} isScrollable>
      <GoBackButton onClick={onBackClick} className={styles.backButton} />
      <h1 className={styles.title}>{t("pickup.timeSlotPage.title")}</h1>
      <div>
        <OptionCard
          title={getTitle(data[0].pickupStartAt)}
          info={getInfo(data[0].pickupStartAt)}
          onClick={() => onSelectOption(data[0].pickupStartAt)}
          suggested
        />

        <div className={styles.otherOptions}>
          <h4>{t("pickup.timeSlotPage.otherOptions")}</h4>
          <div className={styles.cardList}>
            {data.slice(1).map((slot) => (
              <OptionCard
                key={slot.pickupStartAt}
                title={getTitle(slot.pickupStartAt)}
                info={getInfo(slot.pickupStartAt)}
                onClick={() => onSelectOption(slot.pickupStartAt)}
              />
            ))}
          </div>
        </div>
      </div>
      <StillThereModal />
    </Layout>
  );
};

export default PickupTimeSlotPage;
